// src/components/ICT/UserManagement.jsx

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Grid,
  Paper,
  Button,
  Chip,
  IconButton,
  TextField,
  Alert,
  Snackbar,
  Fade,
  Avatar,
  useTheme,
  alpha,
  CircularProgress,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Stack,
  Tooltip,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  InputAdornment,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Menu,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Work as WorkIcon,
  PhotoCamera as PhotoCameraIcon,
  DeleteOutline as DeleteOutlineIcon,
  CloudUpload as CloudUploadIcon,
  Videocam as VideocamIcon,
  CameraAlt as CameraAltIcon,
  Badge as BadgeIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Login as LoginIcon,
} from '@mui/icons-material';

import ICTHeader from '../../components/ICT/Header';
import ICTSidebar from '../../components/ICT/Sidebar';
import ICTFooter from '../../components/ICT/Footer';
import Preloader from '../../theme/Preloader';

// ============================================
// API CLIENT
// ============================================

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
const API_PREFIX   = process.env.REACT_APP_API_PREFIX   || '/api';
const FULL_API_URL = `${API_BASE_URL}${API_PREFIX}`;

const api = axios.create({
  baseURL: FULL_API_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 30000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const extractError = (err) => {
  const d = err?.response?.data;
  if (!d) return err.message || 'Request failed';
  if (typeof d === 'string') return d;
  if (d.errors) {
    const first = Object.values(d.errors)[0];
    return Array.isArray(first) ? first[0] : String(first);
  }
  return d.message || 'Request failed';
};

const apiListUsers = async (params = {}) => {
  try {
    const { data } = await api.get('/users', { params });
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiCreateUser = async (payload) => {
  try {
    const { data } = await api.post('/users', payload);
    return { success: true, user: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiUpdateUser = async (id, payload) => {
  try {
    const { data } = await api.put(`/users/${id}`, payload);
    return { success: true, user: data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiDeleteUser = async (id) => {
  try {
    const { data } = await api.delete(`/users/${id}`);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiToggleActive = async (id) => {
  try {
    const { data } = await api.post(`/users/${id}/toggle-active`);
    return { success: true, ...data };
  } catch (err) { return { success: false, error: extractError(err) }; }
};

const apiGetRoles = async () => {
  try { const { data } = await api.get('/roles'); return { success: true, data }; }
  catch (err) { return { success: false, error: extractError(err) }; }
};

const apiGetDepartments = async () => {
  try { const { data } = await api.get('/departments'); return { success: true, data }; }
  catch (err) { return { success: false, error: extractError(err) }; }
};

const apiGetClasses = async () => {
  try { const { data } = await api.get('/classes'); return { success: true, data }; }
  catch (err) { return { success: false, error: extractError(err) }; }
};

// ============================================
// CONSTANTS
// ============================================

const SELECT_MIN_WIDTH = 160;
const MAX_IMAGE_BYTES = 2 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const FALLBACK_ROLES = [
  { value: 'admin',   label: 'Administrator' },
  { value: 'manager', label: 'Manager' },
  { value: 'vendor',  label: 'Vendor' },
  { value: 'finance', label: 'Finance' },
  { value: 'ict',     label: 'ICT Officer' },
  { value: 'cashier', label: 'Cashier' },
  { value: 'waiter',  label: 'Waiter' },
  { value: 'kitchen', label: 'Kitchen Staff' },
  { value: 'store_keeper', label: 'Store Keeper' },
  { value: 'auditor', label: 'Auditor' },
  { value: 'hr',      label: 'HR Personnel' },
];

const FALLBACK_DEPARTMENTS = [
  { value: 'Management', label: 'Management' },
  { value: 'Sales',      label: 'Sales' },
  { value: 'Finance',    label: 'Finance' },
  { value: 'IT',         label: 'IT' },
  { value: 'HR',         label: 'Human Resources' },
  { value: 'Operations', label: 'Operations' },
  { value: 'Kitchen',    label: 'Kitchen' },
  { value: 'Marketing',  label: 'Marketing' },
];

const FALLBACK_CLASSES = [
  { value: 'JSS1', label: 'JSS 1' },
  { value: 'JSS2', label: 'JSS 2' },
  { value: 'JSS3', label: 'JSS 3' },
  { value: 'SSS1', label: 'SSS 1' },
  { value: 'SSS2', label: 'SSS 2' },
  { value: 'SSS3', label: 'SSS 3' },
];

// ============================================
// UTILITIES
// ============================================

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const getInitials = (firstName, otherNames = '') => {
  const name = `${firstName} ${otherNames}`.trim();
  if (!name) return 'U';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
};

const getRoleColor = (role) => {
  const colors = {
    admin: '#F44336', manager: '#1976D2', vendor: '#FF9800',
    finance: '#2E7D32', ict: '#00BCD4', cashier: '#2E7D32',
    waiter: '#FF9800', kitchen: '#FF5722', store_keeper: '#8BC34A',
    auditor: '#3F51B5', hr: '#E91E63', student: '#9C27B0',
  };
  return colors[role?.toLowerCase()] || '#757575';
};

const generateStaffId = () => `ACCE/STF${Math.floor(10000 + Math.random() * 90000)}`;
const generateStudentId = () => `ACCE/STU${Math.floor(10000 + Math.random() * 90000)}`;

const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read image file'));
    reader.readAsDataURL(file);
  });

// ============================================
// MAIN COMPONENT
// ============================================

const UserManagement = () => {
  const theme = useTheme();

  // ── Page state ──
  const [activeMenu, setActiveMenu] = useState('users');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);

  // ── Users ──
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [accountTypeFilter, setAccountTypeFilter] = useState('');

  // ── Dropdown options ──
  const [ROLE_OPTIONS, setRoleOptions] = useState(FALLBACK_ROLES);
  const [DEPARTMENT_OPTIONS, setDepartmentOptions] = useState(FALLBACK_DEPARTMENTS);
  const [CLASS_OPTIONS, setClassOptions] = useState(FALLBACK_CLASSES);

  // ── Dialogs ──
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  // ── Profile detail dialog ──
  const [profileUser, setProfileUser] = useState(null);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  // ── Account type ──
  const [accountTypeTab, setAccountTypeTab] = useState(0);

  // ── Image ──
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarError, setAvatarError] = useState('');
  const fileInputRef = useRef(null);

  const [imageSourceMenuOpen, setImageSourceMenuOpen] = useState(false);
  const imageSourceAnchorRef = useRef(null);

  const [webcamOpen, setWebcamOpen] = useState(false);
  const [webcamError, setWebcamError] = useState('');
  const [webcamReady, setWebcamReady] = useState(false);
  const [webcamStream, setWebcamStream] = useState(null);
  const videoRef = useRef(null);

  // ── Form ──
  const emptyForm = (type = 'student') => ({
    account_type: type,
    staff_id: type === 'student' ? generateStudentId() : generateStaffId(),
    first_name: '',
    other_names: '',
    department: '',
    phone: '',
    email: '',
    gender: '',
    password: '',
    confirm_password: '',
    role: '',
    is_active: true,
  });

  const [formData, setFormData] = useState(() => emptyForm('student'));
  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Snackbar ──
  const [snackbar, setSnackbar] = useState({
    open: false, message: '', severity: 'success',
  });

  const pushSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);
  const closeSnackbar = useCallback(
    () => setSnackbar((s) => ({ ...s, open: false })), []
  );

  // ════════════════════════════════════════════
  // FETCH
  // ════════════════════════════════════════════

  const fetchDropdowns = useCallback(async () => {
    const [roles, depts, classes] = await Promise.all([
      apiGetRoles(),
      apiGetDepartments(),
      apiGetClasses(),
    ]);

    if (roles.success && Array.isArray(roles.data) && roles.data.length > 0) {
      setRoleOptions(roles.data);
    }
    if (depts.success && Array.isArray(depts.data) && depts.data.length > 0) {
      setDepartmentOptions(depts.data);
    }
    if (classes.success && Array.isArray(classes.data) && classes.data.length > 0) {
      setClassOptions(classes.data);
    }
  }, []);

  const fetchUsers = useCallback(async () => {
    setIsFetching(true);

    const params = {
      page: page + 1,
      page_size: rowsPerPage,
    };
    if (searchQuery)         params.search = searchQuery;
    if (roleFilter)          params.role = roleFilter;
    if (accountTypeFilter)   params.account_type = accountTypeFilter;

    const res = await apiListUsers(params);

    if (!res.success) {
      pushSnackbar(res.error || 'Failed to load users', 'error');
      setUsers([]);
      setTotalUsers(0);
      setIsFetching(false);
      return;
    }

    setUsers(res.users || []);
    setTotalUsers(res.total || 0);
    setIsFetching(false);
  }, [page, rowsPerPage, searchQuery, roleFilter, accountTypeFilter, pushSnackbar]);

  useEffect(() => {
    (async () => {
      await Promise.all([fetchUsers(), fetchDropdowns()]);
      setIsLoading(false);
    })();
  }, [fetchUsers, fetchDropdowns]);

  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    fetchUsers();
  }, [page, rowsPerPage, roleFilter, accountTypeFilter, fetchUsers]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      fetchUsers();
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // ════════════════════════════════════════════
  // HANDLERS
  // ════════════════════════════════════════════

  const handleRefresh = useCallback(async () => {
    await Promise.all([fetchUsers(), fetchDropdowns()]);
    pushSnackbar('Data refreshed.', 'success');
  }, [fetchUsers, fetchDropdowns, pushSnackbar]);

  const handleOpenCreateDialog = useCallback(() => {
    setEditingUser(null);
    setAccountTypeTab(0);
    setFormData(emptyForm('student'));
    setFormErrors({});
    setShowPassword(false);
    setAvatarPreview(null);
    setAvatarError('');
    setUserDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((user) => {
    const type = user.account_type || 'staff';
    setEditingUser(user);
    setAccountTypeTab(type === 'student' ? 0 : 1);
    setFormData({
      account_type: type,
      staff_id: user.staff_id || '',
      first_name: user.first_name || '',
      other_names: user.other_names || '',
      department: user.department || '',
      phone: user.phone || '',
      email: user.email || '',
      gender: user.gender || '',
      password: '',
      confirm_password: '',
      role: user.role || '',
      is_active: user.is_active ?? true,
    });
    setFormErrors({});
    setShowPassword(false);
    setAvatarPreview(user.avatar || null);
    setAvatarError('');
    setUserDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setUserDialogOpen(false);
    setEditingUser(null);
    setFormErrors({});
    setAvatarPreview(null);
    setAvatarError('');
  }, []);

  const handleFormChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: '' }));
  }, [formErrors]);

  const handleAccountTypeTabChange = useCallback((_, newValue) => {
    setAccountTypeTab(newValue);
    const type = newValue === 0 ? 'student' : 'staff';

    setFormData((prev) => ({
      ...prev,
      account_type: type,
      role: type === 'student' ? '' : prev.role,
      department: '',
      staff_id: editingUser
        ? prev.staff_id
        : (type === 'student' ? generateStudentId() : generateStaffId()),
      password: '',
      confirm_password: '',
    }));
    setFormErrors({});
  }, [editingUser]);

  // ── Profile dialog ──
  const handleOpenProfile = useCallback((user) => {
    setProfileUser(user);
    setProfileDialogOpen(true);
  }, []);

  const handleCloseProfile = useCallback(() => {
    setProfileDialogOpen(false);
    setProfileUser(null);
  }, []);

  // ── Image: source picker ──
  const openImageSourceMenu = useCallback((e) => {
    imageSourceAnchorRef.current = e.currentTarget;
    setImageSourceMenuOpen(true);
  }, []);

  const closeImageSourceMenu = useCallback(() => {
    setImageSourceMenuOpen(false);
  }, []);

  const handleChooseUpload = useCallback(() => {
    closeImageSourceMenu();
    setTimeout(() => fileInputRef.current?.click(), 50);
  }, [closeImageSourceMenu]);

  const handleImageChange = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!file) return;

    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setAvatarError('Unsupported file type. Use JPG, PNG, WEBP, or GIF.');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setAvatarError('Image is too large. Max 2 MB.');
      return;
    }

    try {
      const dataUrl = await readFileAsDataUrl(file);
      setAvatarPreview(dataUrl);
      setAvatarError('');
    } catch (err) {
      setAvatarError(err.message || 'Failed to read file');
    }
  }, []);

  const handleRemoveImage = useCallback(() => {
    setAvatarPreview(null);
    setAvatarError('');
  }, []);

  // ── Webcam ──
  const stopWebcam = useCallback(() => {
    setWebcamStream((prev) => {
      if (prev) {
        try { prev.getTracks().forEach((t) => t.stop()); } catch (e) { /* noop */ }
      }
      return null;
    });
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setWebcamReady(false);
  }, []);

  const startWebcam = useCallback(async () => {
    setWebcamError('');
    setWebcamReady(false);

    if (webcamStream) {
      try { webcamStream.getTracks().forEach((t) => t.stop()); } catch (e) { /* noop */ }
    }

    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera API not supported in this browser.');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 640 }, height: { ideal: 480 }, facingMode: 'user' },
        audio: false,
      });

      setWebcamStream(stream);
    } catch (err) {
      const msg =
        err?.name === 'NotAllowedError' ? 'Camera permission denied.' :
        err?.name === 'NotFoundError'   ? 'No camera found on this device.' :
        err?.name === 'NotReadableError' ? 'Camera is in use by another app.' :
        err?.message || 'Failed to access the camera.';
      setWebcamError(msg);
    }
  }, [webcamStream]);

  useEffect(() => {
    if (!webcamOpen) return;
    const video = videoRef.current;
    const stream = webcamStream;
    if (!video || !stream) return;

    video.srcObject = stream;
    const markReady = () => setWebcamReady(true);

    if (video.readyState >= 1) {
      markReady();
      video.play().catch(() => {});
    } else {
      video.onloadedmetadata = () => {
        markReady();
        video.play().catch(() => {});
      };
    }

    return () => { if (video) video.onloadedmetadata = null; };
  }, [webcamOpen, webcamStream]);

  useEffect(() => {
    if (!webcamOpen && webcamStream) stopWebcam();
  }, [webcamOpen, webcamStream, stopWebcam]);

  useEffect(() => {
    return () => {
      setWebcamStream((prev) => {
        if (prev) {
          try { prev.getTracks().forEach((t) => t.stop()); } catch (e) { /* noop */ }
        }
        return null;
      });
    };
  }, []);

  const handleChooseWebcam = useCallback(() => {
    closeImageSourceMenu();
    setWebcamError('');
    setWebcamReady(false);
    setWebcamOpen(true);
    setTimeout(() => { startWebcam(); }, 150);
  }, [closeImageSourceMenu, startWebcam]);

  const handleCloseWebcam = useCallback(() => {
    stopWebcam();
    setWebcamOpen(false);
    setWebcamError('');
  }, [stopWebcam]);

  const handleCaptureWebcam = useCallback(() => {
    const video = videoRef.current;
    if (!video || !video.videoWidth || !video.videoHeight) {
      setWebcamError('Camera stream not ready yet — please wait a moment.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    setAvatarPreview(dataUrl);
    setAvatarError('');
    handleCloseWebcam();
  }, [handleCloseWebcam]);

  // ════════════════════════════════════════════
  // VALIDATION
  // ════════════════════════════════════════════
  const validateForm = useCallback(() => {
    const errors = {};
    const isEditing = !!editingUser;
    const isStudent = formData.account_type === 'student';

    if (!formData.staff_id.trim()) errors.staff_id = 'ID is required';
    if (!formData.first_name.trim()) errors.first_name = 'First name is required';

    if (!formData.department) {
      errors.department = isStudent ? 'Class is required' : 'Department is required';
    }

    if (!isStudent) {
      if (!formData.phone.trim()) {
        errors.phone = 'Phone number is required';
      } else if (!/^[0-9+\-\s()]{10,20}$/.test(formData.phone)) {
        errors.phone = 'Invalid phone number format';
      }
    } else if (formData.phone.trim() && !/^[0-9+\-\s()]{10,20}$/.test(formData.phone)) {
      errors.phone = 'Invalid phone number format';
    }

    if (!isStudent) {
      if (!formData.email) {
        errors.email = 'Email is required';
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
        errors.email = 'Invalid email format';
      }
    } else if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (!formData.gender) errors.gender = 'Gender is required';
    if (!isStudent && !formData.role) errors.role = 'Role is required';

    const passwordRequired = !isStudent && !isEditing;

    if (passwordRequired || formData.password) {
      if (!formData.password) {
        errors.password = 'Password is required';
      } else if (formData.password.length < 8) {
        errors.password = 'Password must be at least 8 characters';
      } else if (!/\d/.test(formData.password)) {
        errors.password = 'Password must contain at least one digit';
      } else if (!/[A-Z]/.test(formData.password)) {
        errors.password = 'Password must contain an uppercase letter';
      } else if (!/[a-z]/.test(formData.password)) {
        errors.password = 'Password must contain a lowercase letter';
      }

      if (formData.password !== formData.confirm_password) {
        errors.confirm_password = 'Passwords do not match';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData, editingUser]);

  // ════════════════════════════════════════════
  // SUBMIT
  // ════════════════════════════════════════════
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);

    const isStudent = formData.account_type === 'student';

    const payload = {
      account_type: formData.account_type,
      staff_id:     formData.staff_id.trim(),
      first_name:   formData.first_name.trim(),
      other_names:  formData.other_names.trim() || null,
      department:   formData.department,
      phone:        formData.phone.trim() || null,
      email:        formData.email.trim() || null,
      gender:       formData.gender,
      is_active:    formData.is_active,
      avatar:       avatarPreview || null,
    };

    if (!isStudent) {
      payload.role = formData.role;
    }

    if (formData.password) {
      payload.password = formData.password;
      payload.password_confirmation = formData.confirm_password;
    }

    const res = editingUser
      ? await apiUpdateUser(editingUser.id, payload)
      : await apiCreateUser(payload);

    setIsSubmitting(false);

    if (!res.success) {
      pushSnackbar(res.error, 'error');
      return;
    }

    pushSnackbar(
      editingUser
        ? 'User updated successfully.'
        : `${isStudent ? 'Student' : 'Staff'} created successfully.`,
      'success'
    );

    handleCloseDialog();
    fetchUsers();
  }, [formData, editingUser, avatarPreview, validateForm, handleCloseDialog, fetchUsers, pushSnackbar]);

  // ════════════════════════════════════════════
  // DELETE / TOGGLE
  // ════════════════════════════════════════════
  const handleDeleteClick = useCallback((user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!userToDelete) return;

    const res = await apiDeleteUser(userToDelete.id);

    if (!res.success) {
      pushSnackbar(res.error, 'error');
      return;
    }

    pushSnackbar(res.message || 'User deactivated.', 'success');
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    fetchUsers();
  }, [userToDelete, fetchUsers, pushSnackbar]);

  const handleToggleActive = useCallback(async (user) => {
    const res = await apiToggleActive(user.id);

    if (!res.success) {
      pushSnackbar(res.error, 'error');
      return;
    }

    pushSnackbar(res.message || 'User status updated.', 'success');
    fetchUsers();
  }, [fetchUsers, pushSnackbar]);

  const handleChangePage = useCallback((_, newPage) => setPage(newPage), []);
  const handleChangeRowsPerPage = useCallback((e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  }, []);

  // ════════════════════════════════════════════
  // RENDER HELPERS
  // ════════════════════════════════════════════

  const renderStatsCards = () => {
    const total = totalUsers;
    const active = users.filter((u) => u.is_active).length;
    const inactive = users.length - active;
    const students = users.filter((u) => u.account_type === 'student').length;
    const staff = users.filter((u) => u.account_type === 'staff').length;

    const stats = [
      { label: 'Total Users',       value: total,             icon: <PeopleIcon />,      color: '#1976D2' },
      { label: 'Students',          value: students,          icon: <SchoolIcon />,      color: '#9C27B0' },
      { label: 'Staff',             value: staff,             icon: <WorkIcon />,        color: '#00897B' },
      { label: 'Active / Inactive', value: `${active}/${inactive}`, icon: <CheckCircleIcon />, color: '#2E7D32' },
    ];

    return (
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {stats.map((stat, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Paper elevation={0} sx={{
              p: { xs: 1.5, sm: 2.5 }, borderRadius: '14px',
              border: `1px solid ${alpha(stat.color, 0.15)}`,
              bgcolor: alpha(stat.color, 0.05),
              display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 },
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'translateY(-3px)', boxShadow: theme.shadows[3] },
            }}>
              <Avatar sx={{
                bgcolor: alpha(stat.color, 0.15), color: stat.color,
                width: { xs: 36, sm: 48 }, height: { xs: 36, sm: 48 },
              }}>{stat.icon}</Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={600}
                  sx={{ fontSize: { xs: '0.55rem', sm: '0.8rem' }, display: 'block', whiteSpace: 'nowrap' }}>
                  {stat.label}
                </Typography>
                <Typography variant="h6" fontWeight={700}
                  sx={{ fontSize: { xs: '0.85rem', sm: '1.2rem' }, lineHeight: 1.2 }}>
                  {stat.value}
                </Typography>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderFilters = () => (
    <Paper elevation={0} sx={{
      p: { xs: 1.5, sm: 2.5 }, borderRadius: '14px', mb: 3,
      border: `1px solid ${alpha('#000', 0.06)}`,
      display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
      gap: { xs: 1.5, sm: 2 }, alignItems: { xs: 'stretch', sm: 'center' },
      flexWrap: 'wrap',
    }}>
      <TextField
        size="small" placeholder="Search users..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ flex: { xs: 1, sm: 2 }, minWidth: { xs: '100%', sm: '220px' },
          '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
            </InputAdornment>
          ),
        }}
      />

      <FormControl size="small" sx={{ minWidth: SELECT_MIN_WIDTH }}>
        <Select value={accountTypeFilter} onChange={(e) => { setAccountTypeFilter(e.target.value); setPage(0); }} displayEmpty
          sx={{ borderRadius: '10px' }}>
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="student">Students</MenuItem>
          <MenuItem value="staff">Staff</MenuItem>
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: SELECT_MIN_WIDTH }}>
        <Select value={roleFilter} onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }} displayEmpty
          sx={{ borderRadius: '10px' }}>
          <MenuItem value="">All Roles</MenuItem>
          {ROLE_OPTIONS.map((r) => (<MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>))}
        </Select>
      </FormControl>

      <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenCreateDialog}
        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700,
          px: { xs: 2, sm: 3 }, py: { xs: 0.75, sm: 1 }, whiteSpace: 'nowrap',
          bgcolor: '#1976D2', '&:hover': { bgcolor: '#1565C0' } }}>
        Add User
      </Button>

      <Button variant="outlined"
        startIcon={isFetching ? <CircularProgress size={20} /> : <RefreshIcon />}
        onClick={handleRefresh} disabled={isFetching}
        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700,
          px: { xs: 1.5, sm: 2 }, py: { xs: 0.75, sm: 1 }, whiteSpace: 'nowrap',
          borderColor: '#1976D2', color: '#1976D2',
          '&:hover': { borderColor: '#1565C0', bgcolor: alpha('#1976D2', 0.08) } }}>
        {isFetching ? 'Loading...' : 'Refresh'}
      </Button>
    </Paper>
  );

  const renderUsersTable = () => {
    if (isFetching && users.length === 0) {
      return (
        <Paper sx={{ p: { xs: 2, sm: 3 }, borderRadius: '14px' }}>
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1, borderRadius: '8px' }} />
          ))}
        </Paper>
      );
    }

    if (users.length === 0) {
      return (
        <Paper sx={{ p: { xs: 3, sm: 5 }, borderRadius: '14px', textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No users found. Click <strong>Add User</strong> to create one.
          </Typography>
        </Paper>
      );
    }

    return (
      <Paper sx={{ borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table stickyHeader size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['User', 'ID', 'Type', 'Contact', 'Department / Class', 'Role', 'Status', 'Actions'].map((h) => (
                  <TableCell key={h}
                    sx={{ fontSize: { xs: '0.65rem', sm: '0.8rem' }, fontWeight: 800,
                      minWidth: 90, textAlign: h === 'Actions' ? 'center' : 'left' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((user) => {
                const roleColor = getRoleColor(user.role);
                const initials = getInitials(user.first_name, user.other_names);
                const isStudent = user.account_type === 'student';

                return (
                  <TableRow
                    key={user.id}
                    hover
                    onClick={() => handleOpenProfile(user)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      '&:hover': { bgcolor: alpha(isStudent ? '#9C27B0' : '#00897B', 0.04) },
                    }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar
                          src={user.avatar || undefined}
                          alt={`${user.first_name} ${user.other_names || ''}`}
                          sx={{
                            width: { xs: 36, sm: 44 }, height: { xs: 36, sm: 44 },
                            bgcolor: alpha(isStudent ? '#9C27B0' : roleColor, 0.12),
                            color: isStudent ? '#9C27B0' : roleColor,
                            fontSize: { xs: '0.72rem', sm: '0.88rem' }, fontWeight: 700,
                            border: `2px solid ${alpha(isStudent ? '#9C27B0' : roleColor, 0.2)}`,
                          }}>
                          {initials}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" fontWeight={700}
                            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' },
                              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {user.first_name} {user.other_names || ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary"
                            sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' }, display: 'block' }}>
                            {user.email || '—'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Chip size="small" label={user.staff_id}
                        sx={{ fontFamily: 'monospace', fontWeight: 700,
                          bgcolor: alpha('#1976D2', 0.08), color: '#1976D2' }} />
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        icon={isStudent ? <SchoolIcon sx={{ fontSize: 14 }} /> : <WorkIcon sx={{ fontSize: 14 }} />}
                        label={isStudent ? 'Student' : 'Staff'}
                        sx={{
                          fontWeight: 700,
                          bgcolor: alpha(isStudent ? '#9C27B0' : '#00897B', 0.12),
                          color: isStudent ? '#9C27B0' : '#00897B',
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      <Typography variant="caption" sx={{ fontSize: { xs: '0.6rem', sm: '0.75rem' },
                        display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        {user.phone || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary"
                        sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' }, display: 'block' }}>
                        Joined: {formatDate(user.created_at)}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Chip size="small" label={user.department || '—'}
                        sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' }, fontWeight: 600,
                          bgcolor: alpha(isStudent ? '#2196F3' : '#9C27B0', 0.08),
                          color: isStudent ? '#1565C0' : '#9C27B0' }} />
                    </TableCell>

                    <TableCell>
                      <Chip size="small" label={user.role}
                        sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' }, fontWeight: 700,
                          textTransform: 'capitalize',
                          bgcolor: alpha(roleColor, 0.12), color: roleColor }} />
                    </TableCell>

                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <FormControlLabel
                        control={
                          <Switch checked={!!user.is_active}
                            onChange={() => handleToggleActive(user)} size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2E7D32' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2E7D32' },
                            }} />
                        }
                        label={
                          <Typography variant="caption"
                            sx={{ fontSize: { xs: '0.55rem', sm: '0.7rem' }, fontWeight: 700,
                              color: user.is_active ? '#2E7D32' : '#F44336' }}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Typography>
                        }
                        sx={{ mr: 0 }}
                      />
                    </TableCell>

                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        <Tooltip title="Edit User">
                          <IconButton size="small" onClick={() => handleOpenEditDialog(user)}
                            sx={{ color: '#1976D2', '&:hover': { bgcolor: alpha('#1976D2', 0.08) } }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete User">
                          <IconButton size="small" onClick={() => handleDeleteClick(user)}
                            sx={{ color: '#F44336', '&:hover': { bgcolor: alpha('#F44336', 0.08) } }}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div" count={totalUsers} page={page}
          onPageChange={handleChangePage} rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            '& .MuiTablePagination-select': { fontSize: { xs: '0.6rem', sm: '0.875rem' } },
            '& .MuiTablePagination-displayedRows': { fontSize: { xs: '0.55rem', sm: '0.875rem' } },
          }}
        />
      </Paper>
    );
  };

  // ── User dialog ──
  const renderUserDialog = () => {
    const isEditing = !!editingUser;
    const isStudent = accountTypeTab === 0;

    return (
      <Dialog open={userDialogOpen} onClose={handleCloseDialog}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', maxHeight: '90vh', p: { xs: 1, sm: 2 } } }}>
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`, pb: 2,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          bgcolor: alpha('#1976D2', 0.04),
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonAddIcon sx={{ color: '#1976D2' }} />
            <Typography variant="h6" component="span" fontWeight={800}
              sx={{ fontSize: { xs: '1rem', sm: '1.2rem' } }}>
              {isEditing ? 'Edit User' : 'Create New User'}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} size="small"><CloseIcon /></IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Tabs
            value={accountTypeTab}
            onChange={handleAccountTypeTabChange}
            variant="fullWidth"
            sx={{
              mb: 3,
              border: `1px solid ${alpha('#000', 0.08)}`,
              borderRadius: 2,
              minHeight: 48,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                minHeight: 48,
                fontSize: { xs: '0.8rem', sm: '0.9rem' },
              },
              '& .Mui-selected': {
                color: isStudent ? '#9C27B0 !important' : '#00897B !important',
              },
              '& .MuiTabs-indicator': {
                backgroundColor: isStudent ? '#9C27B0' : '#00897B',
              },
            }}
          >
            <Tab icon={<SchoolIcon />} iconPosition="start" label="Student" />
            <Tab icon={<WorkIcon />} iconPosition="start" label="Staff" />
          </Tabs>

          <Box sx={{
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: 1, mb: 3,
          }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={avatarPreview || undefined}
                sx={{
                  width: 100, height: 100,
                  bgcolor: alpha(isStudent ? '#9C27B0' : '#00897B', 0.12),
                  color: isStudent ? '#9C27B0' : '#00897B',
                  fontSize: '2rem', fontWeight: 800,
                  border: `3px solid ${alpha(isStudent ? '#9C27B0' : '#00897B', 0.25)}`,
                }}>
                {getInitials(formData.first_name, formData.other_names)}
              </Avatar>

              <IconButton
                onClick={openImageSourceMenu}
                size="small"
                sx={{
                  position: 'absolute', bottom: 0, right: -4,
                  bgcolor: isStudent ? '#9C27B0' : '#00897B',
                  color: '#fff',
                  width: 32, height: 32,
                  border: '2px solid #fff',
                  '&:hover': { bgcolor: isStudent ? '#7B1FA2' : '#00695C' },
                }}
              >
                <PhotoCameraIcon sx={{ fontSize: 16 }} />
              </IconButton>

              {avatarPreview && (
                <Tooltip title="Remove image">
                  <IconButton
                    onClick={handleRemoveImage}
                    size="small"
                    sx={{
                      position: 'absolute', top: -4, right: -4,
                      bgcolor: '#F44336', color: '#fff',
                      width: 24, height: 24,
                      border: '2px solid #fff',
                      '&:hover': { bgcolor: '#D32F2F' },
                    }}
                  >
                    <DeleteOutlineIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPTED_IMAGE_TYPES.join(',')}
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />

            <Menu
              anchorEl={imageSourceAnchorRef.current}
              open={imageSourceMenuOpen}
              onClose={closeImageSourceMenu}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              transformOrigin={{ vertical: 'top', horizontal: 'center' }}
              PaperProps={{ sx: { borderRadius: 2, minWidth: 220, mt: 1 } }}
            >
              <MenuItem onClick={handleChooseUpload} sx={{ fontWeight: 600, py: 1.2 }}>
                <CloudUploadIcon sx={{ mr: 1.5, color: '#1976D2' }} />
                Upload from device
              </MenuItem>
              <MenuItem onClick={handleChooseWebcam} sx={{ fontWeight: 600, py: 1.2 }}>
                <VideocamIcon sx={{ mr: 1.5, color: '#00897B' }} />
                Use webcam
              </MenuItem>
            </Menu>

            <Button
              size="small"
              onClick={openImageSourceMenu}
              startIcon={<PhotoCameraIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: isStudent ? '#9C27B0' : '#00897B',
              }}
            >
              {avatarPreview ? 'Change Image' : 'Add Image (optional)'}
            </Button>

            {avatarError && (
              <Typography variant="caption" color="error" sx={{ fontWeight: 600 }}>
                {avatarError}
              </Typography>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label={isStudent ? 'Student ID' : 'Staff ID'}
                value={formData.staff_id}
                onChange={(e) => handleFormChange('staff_id', e.target.value)}
                error={!!formErrors.staff_id}
                helperText={formErrors.staff_id || 'You can type your own ID or use the auto-generated one'}
                required
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="First Name"
                value={formData.first_name}
                onChange={(e) => handleFormChange('first_name', e.target.value)}
                error={!!formErrors.first_name} helperText={formErrors.first_name}
                required sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label="Other Names"
                value={formData.other_names}
                onChange={(e) => handleFormChange('other_names', e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label={isStudent ? 'Email (optional)' : 'Email'}
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                error={!!formErrors.email} helperText={formErrors.email}
                required={!isStudent}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label={isStudent ? 'Phone (optional)' : 'Phone Number'}
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                error={!!formErrors.phone} helperText={formErrors.phone}
                required={!isStudent}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!formErrors.gender}
                sx={{ minWidth: SELECT_MIN_WIDTH }}>
                <InputLabel>Gender</InputLabel>
                <Select value={formData.gender} label="Gender"
                  onChange={(e) => handleFormChange('gender', e.target.value)}
                  sx={{ borderRadius: '10px' }}>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
                {formErrors.gender && <FormHelperText error>{formErrors.gender}</FormHelperText>}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small" required error={!!formErrors.department}
                sx={{ minWidth: SELECT_MIN_WIDTH }}>
                <InputLabel>{isStudent ? 'Class' : 'Department'}</InputLabel>
                <Select
                  value={formData.department}
                  label={isStudent ? 'Class' : 'Department'}
                  onChange={(e) => handleFormChange('department', e.target.value)}
                  sx={{ borderRadius: '10px' }}>
                  {(isStudent ? CLASS_OPTIONS : DEPARTMENT_OPTIONS).map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </Select>
                {formErrors.department && <FormHelperText error>{formErrors.department}</FormHelperText>}
              </FormControl>
            </Grid>

            {!isStudent && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" required error={!!formErrors.role}
                  sx={{ minWidth: SELECT_MIN_WIDTH }}>
                  <InputLabel>Role</InputLabel>
                  <Select value={formData.role} label="Role"
                    onChange={(e) => handleFormChange('role', e.target.value)}
                    sx={{ borderRadius: '10px' }}>
                    {ROLE_OPTIONS.map((r) => (
                      <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                    ))}
                  </Select>
                  {formErrors.role && <FormHelperText error>{formErrors.role}</FormHelperText>}
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Switch checked={formData.is_active}
                    onChange={(e) => handleFormChange('is_active', e.target.checked)}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#2E7D32' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#2E7D32' },
                    }} />
                }
                label={
                  <Typography variant="body2" fontWeight={700}>
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </Typography>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 2 }}>
                {isEditing
                  ? 'Change Password (leave blank to keep current)'
                  : isStudent
                    ? 'Password (optional — a temporary one will be set if left blank)'
                    : 'Set Password'}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label={isStudent ? 'Password (optional)' : 'Password'}
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleFormChange('password', e.target.value)}
                error={!!formErrors.password} helperText={formErrors.password}
                required={!isStudent && !isEditing}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}
                        edge="end" size="small">
                        {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small"
                label="Confirm Password"
                type={showPassword ? 'text' : 'password'}
                value={formData.confirm_password}
                onChange={(e) => handleFormChange('confirm_password', e.target.value)}
                error={!!formErrors.confirm_password}
                helperText={formErrors.confirm_password}
                required={!isStudent && !isEditing}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }} />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`, pt: 2,
          px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, gap: 1, flexWrap: 'wrap',
        }}>
          <Button onClick={handleCloseDialog} disabled={isSubmitting}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3 }}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting}
            startIcon={isSubmitting ? <CircularProgress size={22} color="inherit" /> : (isEditing ? <EditIcon /> : <PersonAddIcon />)}
            sx={{
              bgcolor: isStudent ? '#9C27B0' : '#00897B',
              '&:hover': { bgcolor: isStudent ? '#7B1FA2' : '#00695C' },
              textTransform: 'none', fontWeight: 700, borderRadius: '8px',
              px: { xs: 3, sm: 4 }, py: { xs: 1, sm: 1.25 },
            }}>
            {isSubmitting ? 'Saving...' : (isEditing ? 'Update User' : `Create ${isStudent ? 'Student' : 'Staff'}`)}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ── Profile detail dialog ──
  const renderProfileDialog = () => {
    if (!profileUser) return null;

    const u = profileUser;
    const isStudent = u.account_type === 'student';
    const roleColor = getRoleColor(u.role);
    const accent = isStudent ? '#9C27B0' : '#00897B';
    const initials = getInitials(u.first_name, u.other_names);

    const DetailRow = ({ icon, label, value, color }) => (
      <Box sx={{
        display: 'flex', alignItems: 'flex-start', gap: 1.5,
        py: 1.2,
        borderBottom: `1px dashed ${alpha('#000', 0.06)}`,
        '&:last-of-type': { borderBottom: 'none' },
      }}>
        <Avatar sx={{
          bgcolor: alpha(color || accent, 0.12),
          color: color || accent,
          width: 34, height: 34,
        }}>
          {icon}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}
            sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: '0.4px', fontSize: '0.62rem' }}>
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={700}
            sx={{ wordBreak: 'break-word', color: value ? '#0F172A' : '#94A3B8' }}>
            {value || '—'}
          </Typography>
        </Box>
      </Box>
    );

    return (
      <Dialog open={profileDialogOpen} onClose={handleCloseProfile}
        maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: '20px', overflow: 'hidden', maxHeight: '92vh' } }}>

        {/* Header with large image */}
        <Box sx={{
          position: 'relative',
          pt: 4, pb: 3, px: 3,
          background: `linear-gradient(135deg, ${alpha(accent, 0.08)} 0%, ${alpha(accent, 0.02)} 100%)`,
          borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        }}>
          <IconButton
            onClick={handleCloseProfile}
            size="small"
            sx={{
              position: 'absolute', top: 12, right: 12,
              bgcolor: '#fff',
              border: `1px solid ${alpha('#000', 0.06)}`,
              '&:hover': { bgcolor: '#fff', boxShadow: theme.shadows[2] },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>

          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            gap: 3,
          }}>
            {/* Large avatar */}
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={u.avatar || undefined}
                alt={`${u.first_name} ${u.other_names || ''}`}
                sx={{
                  width: { xs: 140, sm: 160 },
                  height: { xs: 140, sm: 160 },
                  bgcolor: alpha(accent, 0.15),
                  color: accent,
                  fontSize: '3rem',
                  fontWeight: 800,
                  border: `5px solid #fff`,
                  boxShadow: `0 12px 40px ${alpha(accent, 0.25)}`,
                }}
              >
                {initials}
              </Avatar>

              {/* Status dot */}
              <Box sx={{
                position: 'absolute',
                bottom: 8, right: 8,
                width: 26, height: 26, borderRadius: '50%',
                bgcolor: u.is_active ? '#2E7D32' : '#F44336',
                border: '3px solid #fff',
                boxShadow: theme.shadows[2],
              }} />
            </Box>

            {/* Name + chips */}
            <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' }, minWidth: 0 }}>
              <Typography
                variant="h4"
                fontWeight={800}
                sx={{
                  color: '#0F172A',
                  fontSize: { xs: '1.4rem', sm: '1.8rem' },
                  lineHeight: 1.15,
                  wordBreak: 'break-word',
                }}
              >
                {u.first_name} {u.other_names || ''}
              </Typography>

              <Typography variant="body2" color="text.secondary"
                sx={{ mt: 0.5, fontWeight: 600, display: 'flex', alignItems: 'center',
                  justifyContent: { xs: 'center', sm: 'flex-start' }, gap: 0.5 }}>
                <EmailIcon sx={{ fontSize: 16 }} />
                {u.email || 'No email on file'}
              </Typography>

              <Stack
                direction="row"
                spacing={1}
                flexWrap="wrap"
                justifyContent={{ xs: 'center', sm: 'flex-start' }}
                sx={{ mt: 1.5, gap: 1 }}
              >
                <Chip
                  size="small"
                  label={u.staff_id}
                  sx={{
                    fontFamily: 'monospace', fontWeight: 700,
                    bgcolor: alpha('#1976D2', 0.1), color: '#1976D2',
                  }}
                />
                <Chip
                  size="small"
                  icon={isStudent ? <SchoolIcon sx={{ fontSize: 14 }} /> : <WorkIcon sx={{ fontSize: 14 }} />}
                  label={isStudent ? 'Student' : 'Staff'}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(accent, 0.12),
                    color: accent,
                  }}
                />
                <Chip
                  size="small"
                  label={u.role}
                  sx={{
                    fontWeight: 700, textTransform: 'capitalize',
                    bgcolor: alpha(roleColor, 0.12), color: roleColor,
                  }}
                />
                <Chip
                  size="small"
                  label={u.is_active ? 'Active' : 'Inactive'}
                  sx={{
                    fontWeight: 700,
                    bgcolor: alpha(u.is_active ? '#2E7D32' : '#F44336', 0.12),
                    color: u.is_active ? '#2E7D32' : '#F44336',
                  }}
                />
              </Stack>
            </Box>
          </Box>
        </Box>

        {/* Details grid */}
        <DialogContent sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f8fafc' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: '14px', bgcolor: '#fff',
                border: `1px solid ${alpha('#000', 0.06)}`,
              }}>
                <Typography variant="subtitle2" fontWeight={800}
                  sx={{ mb: 1.5, color: accent, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                  Personal Information
                </Typography>

                <DetailRow
                  icon={<PersonIcon sx={{ fontSize: 18 }} />}
                  label="Full Name"
                  value={`${u.first_name} ${u.other_names || ''}`.trim()}
                />
                <DetailRow
                  icon={<BadgeIcon sx={{ fontSize: 18 }} />}
                  label={isStudent ? 'Student ID' : 'Staff ID'}
                  value={u.staff_id}
                  color="#1976D2"
                />
                <DetailRow
                  icon={isStudent ? <SchoolIcon sx={{ fontSize: 18 }} /> : <WorkIcon sx={{ fontSize: 18 }} />}
                  label={isStudent ? 'Class' : 'Department'}
                  value={u.department}
                  color={isStudent ? '#9C27B0' : '#00897B'}
                />
                <DetailRow
                  icon={<PersonIcon sx={{ fontSize: 18 }} />}
                  label="Gender"
                  value={u.gender}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: '14px', bgcolor: '#fff',
                border: `1px solid ${alpha('#000', 0.06)}`,
              }}>
                <Typography variant="subtitle2" fontWeight={800}
                  sx={{ mb: 1.5, color: accent, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                  Contact & Access
                </Typography>

                <DetailRow
                  icon={<EmailIcon sx={{ fontSize: 18 }} />}
                  label="Email Address"
                  value={u.email}
                  color="#1976D2"
                />
                <DetailRow
                  icon={<PhoneIcon sx={{ fontSize: 18 }} />}
                  label="Phone Number"
                  value={u.phone}
                  color="#2E7D32"
                />
                <DetailRow
                  icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
                  label="Role"
                  value={u.role}
                  color={roleColor}
                />
                <DetailRow
                  icon={<CancelIcon sx={{ fontSize: 18 }} />}
                  label="Account Status"
                  value={u.is_active ? 'Active' : 'Inactive'}
                  color={u.is_active ? '#2E7D32' : '#F44336'}
                />
              </Paper>
            </Grid>

            {/* Activity card */}
            <Grid item xs={12}>
              <Paper elevation={0} sx={{
                p: 2, borderRadius: '14px', bgcolor: '#fff',
                border: `1px solid ${alpha('#000', 0.06)}`,
              }}>
                <Typography variant="subtitle2" fontWeight={800}
                  sx={{ mb: 1.5, color: accent, textTransform: 'uppercase', letterSpacing: '0.5px', fontSize: '0.7rem' }}>
                  Account Activity
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={4}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.3 }}>
                      <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Created
                      </Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700}>
                      {formatDateTime(u.created_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={4}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.3 }}>
                      <CalendarIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Last Updated
                      </Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700}>
                      {formatDateTime(u.updated_at)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.3 }}>
                      <LoginIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" fontWeight={700}>
                        Last Login
                      </Typography>
                    </Stack>
                    <Typography variant="body2" fontWeight={700}>
                      {u.last_login ? formatDateTime(u.last_login) : 'Never'}
                    </Typography>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>

        {/* Actions */}
        <DialogActions sx={{
          p: 2,
          borderTop: `1px solid ${alpha('#000', 0.06)}`,
          bgcolor: '#fff',
          gap: 1,
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}>
          <Button
            onClick={() => {
              handleCloseProfile();
              handleDeleteClick(u);
            }}
            startIcon={<DeleteIcon />}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: '10px',
              color: '#F44336',
              '&:hover': { bgcolor: alpha('#F44336', 0.06) },
            }}
          >
            Delete
          </Button>

          <Stack direction="row" spacing={1}>
            <Button
              onClick={() => {
                handleCloseProfile();
                handleToggleActive(u);
              }}
              startIcon={u.is_active ? <CancelIcon /> : <CheckCircleIcon />}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: '10px',
                color: u.is_active ? '#F44336' : '#2E7D32',
                border: `1px solid ${alpha(u.is_active ? '#F44336' : '#2E7D32', 0.4)}`,
                '&:hover': { bgcolor: alpha(u.is_active ? '#F44336' : '#2E7D32', 0.06) },
              }}
            >
              {u.is_active ? 'Deactivate' : 'Activate'}
            </Button>

            <Button
              onClick={() => {
                handleCloseProfile();
                handleOpenEditDialog(u);
              }}
              variant="contained"
              startIcon={<EditIcon />}
              sx={{
                textTransform: 'none', fontWeight: 800, borderRadius: '10px',
                bgcolor: accent,
                '&:hover': { bgcolor: isStudent ? '#7B1FA2' : '#00695C' },
                px: 3,
              }}
            >
              Edit User
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    );
  };

  // ── Webcam modal ──
  const renderWebcamDialog = () => (
    <Dialog
      open={webcamOpen}
      onClose={handleCloseWebcam}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
    >
      <DialogTitle sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        bgcolor: alpha('#00897B', 0.04),
        borderBottom: `1px solid ${alpha('#000', 0.06)}`,
        py: 1.5,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VideocamIcon sx={{ color: '#00897B' }} />
          <Typography variant="h6" component="span" fontWeight={800}
            sx={{ fontSize: '1.05rem' }}>
            Take Photo
          </Typography>
        </Box>
        <IconButton onClick={handleCloseWebcam} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, bgcolor: '#0f172a' }}>
        <Box sx={{
          position: 'relative', width: '100%', aspectRatio: '4/3',
          bgcolor: '#000', borderRadius: 2, overflow: 'hidden',
        }}>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              transform: 'scaleX(-1)',
              display: 'block',
            }}
          />

          {!webcamReady && !webcamError && (
            <Box sx={{
              position: 'absolute', inset: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
              color: '#fff', gap: 2, bgcolor: 'rgba(0,0,0,0.55)',
            }}>
              <CircularProgress sx={{ color: '#00D4FF' }} />
              <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                Starting camera…
              </Typography>
            </Box>
          )}
        </Box>

        {webcamError && (
          <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
            {webcamError}
          </Alert>
        )}

        {!webcamError && webcamReady && (
          <Typography variant="caption" sx={{ display: 'block', mt: 2, color: '#94a3b8', textAlign: 'center' }}>
            Position your face in the frame and click Capture.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{
        borderTop: `1px solid ${alpha('#000', 0.06)}`, pt: 2,
        px: 3, pb: 2, gap: 1, justifyContent: 'space-between',
      }}>
        <Button onClick={handleCloseWebcam} sx={{ textTransform: 'none', fontWeight: 600 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<CameraAltIcon />}
          onClick={handleCaptureWebcam}
          disabled={!webcamReady || !!webcamError}
          sx={{
            textTransform: 'none', fontWeight: 800, borderRadius: '8px',
            bgcolor: '#00897B', '&:hover': { bgcolor: '#00695C' },
            '&.Mui-disabled': { bgcolor: alpha('#00897B', 0.4) },
            px: 3,
          }}
        >
          Capture
        </Button>
      </DialogActions>
    </Dialog>
  );

  // ── Delete dialog ──
  const renderDeleteDialog = () => {
    if (!userToDelete) return null;

    return (
      <Dialog open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: { xs: 1, sm: 2 } } }}>
        <DialogTitle sx={{
          borderBottom: `1px solid ${alpha('#000', 0.06)}`, pb: 2,
          display: 'flex', alignItems: 'center', gap: 1,
          bgcolor: alpha('#F44336', 0.04),
        }}>
          <CancelIcon sx={{ color: '#F44336' }} />
          <Typography variant="h6" component="span" fontWeight={800}>
            Delete User
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: '10px' }}>
            <Typography variant="body2" fontWeight={700}>
              Are you sure you want to delete this user?
            </Typography>
          </Alert>

          <Paper elevation={0} sx={{ p: 2, borderRadius: '12px', bgcolor: alpha('#000', 0.02) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar
                src={userToDelete.avatar || undefined}
                sx={{
                  width: 48, height: 48, bgcolor: alpha('#F44336', 0.12),
                  color: '#F44336', fontWeight: 700,
                }}>
                {getInitials(userToDelete.first_name, userToDelete.other_names)}
              </Avatar>
              <Box>
                <Typography variant="body1" fontWeight={800}>
                  {userToDelete.first_name} {userToDelete.other_names || ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {userToDelete.email || '—'} • {userToDelete.role}
                </Typography>
              </Box>
            </Box>
          </Paper>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will deactivate the user. The account is preserved for audit purposes.
          </Typography>
        </DialogContent>

        <DialogActions sx={{
          borderTop: `1px solid ${alpha('#000', 0.06)}`, pt: 2,
          px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 }, gap: 1,
        }}>
          <Button onClick={() => setDeleteDialogOpen(false)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', px: 3 }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error"
            startIcon={<DeleteIcon />}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '8px', px: 4, py: 1.25 }}>
            Delete User
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // ════════════════════════════════════════════
  // LOADING STATE
  // ════════════════════════════════════════════
  if (isLoading) {
    return <Preloader text="Loading User Management..." size={70} fullScreen />;
  }

  // ════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <ICTSidebar activeMenu={activeMenu} setActiveMenu={setActiveMenu} />

        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
          <ICTHeader />

          <Box sx={{
            flex: 1, overflow: 'auto', p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: '#f8fafc',
            '&::-webkit-scrollbar': { width: 6 },
            '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
            '&::-webkit-scrollbar-thumb': { bgcolor: 'grey.300', borderRadius: 3 },
          }}>
            <Box sx={{
              bgcolor: 'white', borderRadius: '16px', p: { xs: 1.5, sm: 2.5, md: 3 },
              mb: { xs: 1.5, sm: 2.5, md: 3 }, display: 'flex',
              justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2,
              border: `1px solid ${alpha('#1976D2', 0.12)}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
            }}>
              <Box>
                <Typography variant="h4" sx={{
                  color: '#1976D2', fontWeight: 800,
                  fontSize: { xs: '1.4rem', sm: '2rem', md: '2.4rem' },
                }}>
                  👥 User Management
                </Typography>
                <Typography variant="body2" color="text.secondary"
                  sx={{ mt: 0.5, fontWeight: 600 }}>
                  Register students and staff · manage roles and permissions
                </Typography>
              </Box>
            </Box>

            {renderStatsCards()}
            {renderFilters()}
            {renderUsersTable()}

            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Total: {totalUsers} · Showing {users.length}
                {' · '}
                <span style={{ color: '#FF9800', fontWeight: 700 }}>
                  Click any row for full profile
                </span>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <ICTFooter />

      {renderUserDialog()}
      {renderProfileDialog()}
      {renderWebcamDialog()}
      {renderDeleteDialog()}

      <Snackbar open={snackbar.open} autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled"
          sx={{ width: '100%', borderRadius: '10px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UserManagement;